import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MagicWand } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { toast } from "@/hooks/useToast"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { showAIErrorMessages } from "@/modules/chats/lib/show-ai-error-messages"
import { chatHealthInformationCopilotQuery } from "@/modules/chats/services/ai/chatHealthInformationCopilotQuery"
import { useOptionalChatThreadContext } from "@/modules/chats/stores/useChatThreadStore"
import { useHealthInformationCopilotContext } from "@/modules/patients/contexts/health-information-copilot-context"

export const HealthInformationCopilotButton = () => {
  const threadSelected = useOptionalChatThreadContext((state) => state.selectedThread)
  const { t } = useTranslation()
  const setHealthInformationCopilot = useHealthInformationCopilotContext((state) => state.setHealthInformationCopilot)

  const [loading, setLoading] = useState(false)

  const handleHealthInformationCopilotFetch = async () => {
    if (!threadSelected || loading) return

    setLoading(true)
    try {
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", true))

      const aiFeatureId = uuidv4()

      const result = await chatHealthInformationCopilotQuery(threadSelected.patient.country, {
        aiFeatureId: aiFeatureId,
        appointmentId: threadSelected.appointmentId,
        age: threadSelected.patient.age.years,
        gender: threadSelected.patient.gender,
      })

      if (!result) {
        return
      }
      if (result.chatHealthInformationCopilot.result === "SUCCESS") {
        if (
          result.chatHealthInformationCopilot.healthInformation?.conditions.length === 0 &&
          result.chatHealthInformationCopilot.healthInformation?.allergies.length === 0 &&
          result.chatHealthInformationCopilot.healthInformation?.medications.length === 0
        ) {
          toast({
            description: t("chat.aiFeature.healthInformationCopilot.notFound"),
          })
        }

        setHealthInformationCopilot({
          conditions: result.chatHealthInformationCopilot.healthInformation
            ? [...result.chatHealthInformationCopilot.healthInformation.conditions]
            : [],
          allergies: result.chatHealthInformationCopilot.healthInformation
            ? [...result.chatHealthInformationCopilot.healthInformation.allergies]
            : [],
          medications: result.chatHealthInformationCopilot.healthInformation
            ? [...result.chatHealthInformationCopilot.healthInformation.medications]
            : [],
        })
      } else {
        showAIErrorMessages(result.chatHealthInformationCopilot.result)
      }
    } catch (error) {
      toast({
        description: t("chat.ai.errors.UNKNOWN_ERROR"),
      })
      console.error("Chat Health Information Copilot request is failed", error)
    } finally {
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", false))
      setLoading(false)
    }
  }

  return (
    <Button
      onClick={handleHealthInformationCopilotFetch}
      className="border-on-plum-surface bg-plum-surface-subdued text-on-plum-surface hover:bg-plum-surface grid h-fit w-fit cursor-pointer items-center justify-center gap-2 rounded-md border p-1.5 disabled:cursor-not-allowed disabled:opacity-75"
      disabled={loading}
      isLoading={loading}>
      <MagicWand size={24} />
    </Button>
  )
}
